"use client";

import type React from "react";
import { useState, useCallback } from "react";
import {
  FormControl,
  FormLabel,
  Switch,
  Text,
  Link,
  useToast,
  Box,
  Flex,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { agentService } from "../../../services/agentService.ts";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface OptOutSensitiveDataProps {
  agentId: string;
  initialOptOut: boolean;
}

export const OptOutSensitiveData: React.FC<OptOutSensitiveDataProps> = ({
  agentId,
  initialOptOut,
}) => {
  const [optOut, setOptOut] = useState(initialOptOut);
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const updateOptOut = async (checked: boolean) => {
    setIsUpdating(true);
    try {
      await agentService.updateAgentSettings(agentId, {
        opt_out_sensitive_data_storage: checked,
      });

      setOptOut(checked);
      toast({
        title: "Sensitive data storage preference updated",
        status: "success",
        duration: 2000,
      });
    } catch (error: any) {
      console.error("Error updating sensitive data preference:", error);
      toast({
        title: "Error updating preference",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });
      // Revert UI state on error
      setOptOut(!checked);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggle = useCallback(async () => {
    if (isUpdating) return;

    updateOptOut(!optOut);
  }, [agentId, optOut, isUpdating, toast]);

  return (
    <FormControl>
      <Flex justify="space-between" align="center">
        <Box>
          <FormLabel
            fontSize="sm"
            fontWeight="medium"
            color={isDark ? "gray.300" : "gray.700"}
            mb={1}
          >
            Opt Out Sensitive Data Storage
          </FormLabel>
          <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"}>
            Control whether we should store sensitive data.
            {/* {" "} */}
            {/* <Link
              href="https://docs.retellai.com/accounts/privacy-disable"
              color={isDark ? "gray.300" : "gray.600"}
              isExternal
            >
              Learn more <ExternalLinkIcon mx="2px" />
            </Link> */}
          </Text>
        </Box>
        <Switch
          isChecked={optOut}
          onChange={handleToggle}
          isDisabled={isUpdating}
          colorScheme={isDark ? "whiteAlpha" : "blackAlpha"}
          borderColor={isDark ? "gray.500" : "gray.400"}
          sx={{
            ".chakra-switch__track": {
              backgroundColor: optOut
                ? isDark
                  ? "gray.600"
                  : "gray.400"
                : isDark
                ? "gray.700"
                : "gray.300",
            },
          }}
        />
      </Flex>
    </FormControl>
  );
};
